import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { StoreType } from "../../../utils/types";
import { fetchAllLocations } from "../../scheduler/store/scheduler-slice";
import { getLocationHolidays } from "../store/holidays-slice";
import { Holiday, HolidayOccurrence } from "../types";
import {
	getDateKeyInTimeZone,
	getHolidayOccurrences,
	getHolidaysOnDate,
	getIanaTimeZone,
} from "../utils/holiday-dates";

// companies/{id}/locations returns more than the scheduler's Location type
// declares; the time zone is a Windows zone name.
interface LocationWithTimeZone {
	id: number;
	name: string;
	timeZone?: string | null;
}

export interface LocationHolidayOccurrence extends HolidayOccurrence {
	locationId: number;
	locationName: string;
	timeZone: string;
}

const EMPTY: Holiday[] = [];

/**
 * Loads the effective holidays (company + the location's additions) for each
 * of the given locations, plus their time zones, and answers "which holidays
 * fall on this date at this location". Lists are cached in the holidays slice
 * and fetched once per location until an admin change invalidates them.
 */
const useLocationHolidays = (locationIds: number[]) => {
	const dispatch = useDispatch();
	const companyId = useSelector(
		(state: StoreType) => state.user?.userData?.companyId
	);
	const locationHolidays = useSelector(
		(state: StoreType) => state.holidays.locationHolidays
	);
	const allLocations = useSelector(
		(state: StoreType) =>
			state.supervisor.masterData.allLocations as unknown as LocationWithTimeZone[]
	);
	// The scheduler's own location list has names but no time zones
	const filterLocations = useSelector(
		(state: StoreType) => state.supervisor.filterData?.allLocations
	);
	const fetchAllLocationsStatus = useSelector(
		(state: StoreType) => state.supervisor.fetchAllLocations?.status
	);

	const locationIdsKey = [...new Set(locationIds.filter(Boolean))]
		.sort((a, b) => a - b)
		.join(",");
	const uniqueLocationIds = useMemo(
		() => (locationIdsKey ? locationIdsKey.split(",").map(Number) : []),
		[locationIdsKey]
	);

	useEffect(() => {
		uniqueLocationIds.forEach((locationId) => {
			if (!locationHolidays[locationId]) {
				dispatch(getLocationHolidays(locationId));
			}
		});
	}, [dispatch, uniqueLocationIds, locationHolidays]);

	useEffect(() => {
		if (
			uniqueLocationIds.length > 0 &&
			companyId &&
			(allLocations ?? []).length === 0 &&
			fetchAllLocationsStatus === "idle"
		) {
			dispatch(fetchAllLocations(companyId));
		}
	}, [dispatch, uniqueLocationIds, companyId, allLocations, fetchAllLocationsStatus]);

	const getLocation = useCallback(
		(locationId: number): LocationWithTimeZone | undefined =>
			(allLocations ?? []).find((location) => location.id === locationId) ??
			(filterLocations ?? []).find((location) => location.id === locationId),
		[allLocations, filterLocations]
	);

	const getLocationTimeZone = useCallback(
		(locationId: number) => getIanaTimeZone(getLocation(locationId)?.timeZone),
		[getLocation]
	);

	const getHolidaysForLocation = useCallback(
		(locationId: number): Holiday[] =>
			locationHolidays[locationId]?.data ?? EMPTY,
		[locationHolidays]
	);

	// Holidays on the date `instant` falls on at the Location
	const getHolidaysAtInstant = useCallback(
		(locationId: number, instant: Date) => {
			const timeZone = getLocationTimeZone(locationId);
			const dateKey = getDateKeyInTimeZone(instant, timeZone);
			return {
				dateKey,
				timeZone,
				holidays: getHolidaysOnDate(getHolidaysForLocation(locationId), dateKey),
			};
		},
		[getLocationTimeZone, getHolidaysForLocation]
	);

	// Every holiday across the given locations in [fromKey, toKey]
	const getOccurrencesInRange = useCallback(
		(fromKey: string, toKey: string): LocationHolidayOccurrence[] =>
			uniqueLocationIds.flatMap((locationId) =>
				getHolidayOccurrences(
					getHolidaysForLocation(locationId),
					fromKey,
					toKey
				).map((occurrence) => ({
					...occurrence,
					locationId,
					locationName: getLocation(locationId)?.name ?? "",
					timeZone: getLocationTimeZone(locationId),
				}))
			),
		[uniqueLocationIds, getHolidaysForLocation, getLocation, getLocationTimeZone]
	);

	return {
		getLocation,
		getLocationTimeZone,
		getHolidaysForLocation,
		getHolidaysAtInstant,
		getOccurrencesInRange,
	};
};

export default useLocationHolidays;
